import { useQuery } from "@tanstack/react-query";
import { Combobox, ComboboxOption, Tooltip, Button, ComboboxRef } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconChartPie, IconExternalLink, IconSettings2 } from "@tabler/icons-react";
import { getDatamates } from "@/helpers/apis";
import { useChatContext, updateDatamateInSession } from "@altimateai/ui-components/chatbotV2";
import DatamateSettings from "./DatamateSettings";
import { useLocation, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import useIntegrations from "@/pages/teammate/useIntegrations";
import { useConnections } from "@/hooks/useConnections";
import { hasInvalidIntegrationConfig } from "@/helpers/datamateValidation";
import { Teammate } from "@/pages/mcp/mcpSlice";
import { useAppState } from "@/modules/app/useAppContext";
import { StudioTracking } from "@/lib/tracking/studio";

interface DatamatesSelectorProps {}

export const DatamatesSelector: React.FC<DatamatesSelectorProps> = () => {
  // Get chat context for session data and dispatch
  const { isDemoTenant, loggedinUser } = useAppState();
  const { dispatch, currentSessionId, sessions } = useChatContext();
  const location = useLocation();
  const selectedDatamateInLocationState = location.state?.selectedDatamate;
  const comboboxRef = useRef<ComboboxRef>(null);
  const [settingsDatamate, setSettingsDatamate] = useState<Teammate | null>(null);

  // Get selected datamates from current session
  const currentSession = currentSessionId ? sessions[currentSessionId] : null;
  const selectedDatamates = currentSession?.datamates || [];

  useEffect(() => {
    if (selectedDatamateInLocationState && currentSessionId) {
      // If a datamate is provided in location state, select it
      dispatch(
        updateDatamateInSession({
          sessionId: currentSessionId,
          datamates: [selectedDatamateInLocationState],
        })
      );
    }
  }, [selectedDatamateInLocationState, currentSessionId, dispatch]);

  // Fetch datamates
  const { data: datamatesData } = useQuery({
    queryKey: ["datamates"],
    queryFn: () => getDatamates(),
    enabled: !!loggedinUser,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes (no refetch on remount)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const datamates = datamatesData?.datamates || [];

  // Get integrations and connections for validation
  const { allIntegrations, isCustomIntegrationsLoading, isIntegrationsFromServerLoading } =
    useIntegrations();

  // Get all datamate integration IDs for connections hook
  const allDatamateIntegrationIds = datamates.flatMap(
    datamate => datamate?.integrations?.map(int => int.id) || []
  );

  const { connections, isLoading: isConnectionsLoading } = useConnections({
    integrations: allDatamateIntegrationIds,
  });

  const isValidationLoading =
    isCustomIntegrationsLoading || isIntegrationsFromServerLoading || isConnectionsLoading;

  const handleSelect = (datamateId: string) => {
    if (datamateId === "create-new") {
      // Don't handle this case here, let the Link component handle navigation
      return;
    }

    if (currentSessionId) {
      // Check if the clicked datamate is already selected
      const isAlreadySelected = selectedDatamates.includes(datamateId);

      if (isAlreadySelected) {
        // Deselect the datamate by removing it from the array
        dispatch(
          updateDatamateInSession({
            sessionId: currentSessionId,
            datamates: [],
          })
        );
      } else {
        // Select the datamate by replacing the array with just this datamate
        if (selectedDatamates.length > 0) {
          StudioTracking.datamateSwitched(selectedDatamates[0], datamateId);
        }
        dispatch(
          updateDatamateInSession({
            sessionId: currentSessionId,
            datamates: [datamateId],
          })
        );
      }
    }
  };

  const handleOpenSettings = (datamate: Teammate, e: React.MouseEvent) => {
    e.stopPropagation();
    comboboxRef.current?.close();
    // Small delay to allow combobox to close first
    setTimeout(() => {
      setSettingsDatamate(datamate);
    }, 100);
  };

  // Create options array - if no datamates exist, show only "Create New Datamate" option
  const options: ComboboxOption[] =
    datamates.length === 0
      ? isDemoTenant
        ? []
        : [
            {
              value: "create-new",
              label: "Create New Datamate",
              labelNode: (
                <Button
                  asChild
                  variant="ghost"
                  className="al-w-full al-justify-between al-h-auto al-p-0"
                >
                  <Link to="/datamates/create" target="_blank" rel="noopener noreferrer">
                    <span className="al-flex-1 al-truncate al-mr-2">Create New</span>
                    <IconExternalLink size={16} />
                  </Link>
                </Button>
              ),
            },
          ]
      : datamates
          .filter(datamate => datamate.id) // Filter out datamates without IDs for combobox
          .map(datamate => ({
            value: datamate.id!,
            label: datamate.name,
            labelNode: (
              <Stack direction="row" align="center" justify="between" className="al-w-full">
                <span className="al-flex-1 al-truncate al-mr-2">{datamate.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="al-h-6 al-w-6 al-p-0 al-relative"
                  onClick={e => handleOpenSettings(datamate, e)}
                  aria-label="Settings"
                >
                  <IconSettings2 className="al-h-4 al-w-4" />
                  {/* Red dot indicator for invalid config */}
                  {!isValidationLoading &&
                    hasInvalidIntegrationConfig(datamate, allIntegrations, connections) && (
                      <Tooltip title="Missing configurations">
                        <div
                          className="al-absolute al-top-0 al-right-0 al-h-2 al-w-2 al-bg-red-500 al-rounded-full al--translate-y-0.5 al-translate-x-0.5"
                          role="status"
                          aria-label="Missing configurations"
                        />
                      </Tooltip>
                    )}
                </Button>
              </Stack>
            ),
          }));

  const selectedDatamate = datamates.find(d => d.id && selectedDatamates.includes(d.id));
  const selectedValue = selectedDatamate?.id || "";

  // Check if selected datamate has invalid config
  const selectedHasInvalidConfig =
    !isValidationLoading && selectedDatamate
      ? hasInvalidIntegrationConfig(selectedDatamate, allIntegrations, connections)
      : false;

  return (
    <>
      <div className="al-relative">
        <Combobox
          ref={comboboxRef}
          placeholder="Add Datamate"
          searchPlaceholder="Search datamates..."
          options={options}
          value={selectedValue}
          onChange={handleSelect}
          icon={<IconChartPie size={16} aria-hidden="true" />}
          buttonProps={{ className: "!al-w-[12rem]" }}
        />
        {/* Red dot indicator for selected datamate with invalid config */}
        {selectedHasInvalidConfig && (
          <Tooltip title="Missing configurations">
            <div
              className="al-absolute al-top-2 al-right-2 al-h-2 al-w-2 al-bg-red-500 al-rounded-full al-pointer-events-none"
              role="status"
              aria-label="Missing configurations"
            />
          </Tooltip>
        )}
      </div>

      {/* Settings Sheet - rendered outside combobox */}
      {settingsDatamate && (
        <DatamateSettings
          datamate={settingsDatamate}
          open={!!settingsDatamate}
          onOpenChange={open => {
            if (!open) {
              setSettingsDatamate(null);
            }
          }}
        />
      )}
    </>
  );
};
